import React, { Fragment, useState, useEffect, useRef, useLayoutEffect } from "react";
import { Formik, Form } from "formik";
import { gql, useQuery, useMutation } from "urql";
import { sortBy, keyBy, cloneDeep, startCase } from "lodash";
import { Dropdown } from "../../../components/src/Dropdown";
import { useLocation, Link, useSearchParams } from "wouter";
import stableStringify from "json-stable-stringify";
import { Button } from "../../../components/src/Button";
import { Alert, AlertVariant } from "../../../components/src/Alert";

import { emptyObjectId } from "../../../common/src";
import { formatYmdInTimeZone } from "../../../common/src/dateIntl";
import { recursivelyRemoveKey } from "../../../common/src/utils";
import { useAlertTimeOut, usePrevious } from "../../../components/src/common/Utils";
import {
    SubmitButton,
    TextField,
    SelectField,
    DateField,
    NumberField,
    SearchListField,
    TransactionField,
    CheckBoxField,
    NullableDateField,
    TimestampField
} from "../../../components/src/form";

import { YesNoModal } from "../../../components/src/YesNoModal";
import { isValidMongoDBObjectID } from "../../../common/src/utils";
import { formikUrqlErrorFormater } from "../../../components/src/common/formik-urql-error-helper";

import { AttachmentTransactionForm } from "./AttachmentTransactionForm";
import {
    SourceType,
    CurrencyEnum,
    AgreementType,
    PartyType,
    Instrument,
    TransactionStatus,
    TransactionType,
    PartyAccountType,
    InstrumentProductTypeEnum,
    InstrumentTypeEnum,
    InstrumentModelTypeEnum,
    MiniTransaction,
    BestExecutionTypeDescriptions,
    TransactionStatusDescriptions
} from "../types.generated";
import { BestExecutionType, TransactionItemType } from "../types.generated";

export const miniTransactionTypes = [
    TransactionType.StockTrade,
    TransactionType.BondTrade,
    TransactionType.DerivativeTrade,
    "CloseDerivativeTrade",
    "ExpiryDerivativeTrade",
    TransactionType.Collateral,
    TransactionType.Transfer,
    "FxTransfer",
    TransactionType.Dividend,
    TransactionType.Fee,
    TransactionType.Interest,
    TransactionType.Insert,
    TransactionType.FundTrade,
    TransactionType.CreateRedeem
];
const noExternalIdTypes = [
    TransactionType.Collateral,
    TransactionType.Transfer,
    "FxTransfer",
    TransactionType.Fee,
    TransactionType.Interest,
    "CloseDerivativeTrade",
    "ExpiryDerivativeTrade",
    TransactionType.Insert,
    TransactionType.FundTrade
];

const defaultFatFingerTolerance = 50;

const GET_MINI_TRANSACTION = gql`
    query getMiniTransaction($id: GraphQLObjectId) {
        miniTransaction(_id: $id) {
            _id
            type
            accountId
            tradeTimestamp
            effectiveDate
            description
            status
            error
            bestExecutionType
            brokerId
            brokerTradeId
            corporateAction {
                _id
                externalId
            }
            brokerTransaction {
                _id
                externalId
            }
            source
            instrumentId
            externalAccountId
            currency
            valueDate
            settlementAmount
            clientId
            externalId
            quantity
            price
            amount
            commission
            stampDuty
            foreignTax
            dividendPaid
            taxRestitution
            payableReceivable
            reinvestedDividend
            reinvestedRebate
            fxRate
            accruedInterest
            agreementType
            uniqueTradeId
            clearingHouseId
            clearingBrokerId
            secondInstrumentId
            secondAmount
            secondCurrency
            secondAccountId
            bonusShares
            fee
            itemType
            parentId
            carryOwnCostDate
            initialMargin
            loan
            rounding
        }
    }
`;

const GET_DATA = gql`
    query {
        clients: parties(filter: { typeIn: [Client] }) {
            name
            _id
            types
            inLedgerTradeDate
            instruments {
                _id
                name
                longName
                currency
            }
            accounts {
                _id
                name
                type
                description
            }
            externalAccounts {
                _id
                name
            }
        }

        brokers: parties(filter: { typeIn: [Broker] }) {
            _id
            name
            legalEntityIdentifier
        }

        clearingBrokersUnsorted: parties(filter: { typeIn: [ClearingBroker] }) {
            _id
            name
            legalEntityIdentifier
        }

        clearingHousesUnsorted: parties(filter: { typeIn: [ClearingHouse] }) {
            _id
            name
            legalEntityIdentifier
        }
    }
`;

const GET_DERIVATIVE_TRANSACTIONS = gql`
    query getDerivativeTransactions {
        derivativeTransactions: miniTransactions(type: [DerivativeTrade]) {
            _id
            externalId
            source
            instrument {
                model {
                    _t
                }
            }
            type
        }
    }
`;

const GET_INSERT_TRANSACTIONS = gql`
    query getInsertTransactions {
        insertTransactions: miniTransactions(type: [Insert]) {
            _id
            externalId
            source
            brokerId
            agreementType
            quantity
            instrument {
                model {
                    _t
                }
            }
        }
    }
`;

const GET_INSTRUMENTS = gql`
    query instruments($filter: InstrumentFilterInput) {
        instruments(filter: $filter) {
            _id
            isin
            name
            longName
            bloombergTicker
            currency
            productType
        }
    }
`;

const CREATE_MINI_TRANSACTIONS = gql`
    mutation CreateMiniTransactions($input: [CreateMiniTransactionInput!]!) {
        createMiniTransactions(input: $input) {
            _id
            type
            accountId
            tradeTimestamp
            effectiveDate
            description
            status
            error
            bestExecutionType
            brokerId
            brokerTradeId
            corporateAction {
                _id
                externalId
            }
            brokerTransaction {
                _id
                externalId
            }
            source
            instrumentId
            externalAccountId
            currency
            valueDate
            settlementAmount
            clientId
            externalId
            quantity
            price
            amount
            commission
            stampDuty
            foreignTax
            dividendPaid
            taxRestitution
            payableReceivable
            reinvestedDividend
            reinvestedRebate
            fxRate
            accruedInterest
            agreementType
            uniqueTradeId
            clearingHouseId
            clearingBrokerId
            secondInstrumentId
            secondAccountId
            secondAmount
            secondCurrency
            bonusShares
            fee
            itemType
            parentId
            carryOwnCostDate
            initialMargin
            loan
            rounding
        }
    }
`;

const UPDATE_MINI_TRANSACTION = gql`
    mutation UpdateMiniTransaction($input: UpdateMiniTransactionInput!) {
        updateMiniTransaction(input: $input) {
            _id
            type
            accountId
            tradeTimestamp
            effectiveDate
            description
            status
            error
            bestExecutionType
            brokerId
            brokerTradeId
            corporateAction {
                _id
                externalId
            }
            brokerTransaction {
                _id
                externalId
            }
            source
            instrumentId
            externalAccountId
            currency
            valueDate
            settlementAmount
            clientId
            externalId
            quantity
            price
            amount
            commission
            stampDuty
            foreignTax
            dividendPaid
            taxRestitution
            payableReceivable
            reinvestedDividend
            reinvestedRebate
            fxRate
            accruedInterest
            agreementType
            uniqueTradeId
            clearingHouseId
            clearingBrokerId
            secondInstrumentId
            secondAccountId
            secondAmount
            secondCurrency
            bonusShares
            fee
            itemType
            parentId
            carryOwnCostDate
            initialMargin
            loan
            rounding
        }
    }
`;

// const isFunction = (functionToCheck) => functionToCheck && {}.toString.call(functionToCheck) === "[object Function]";

const agreementTypes = [{ key: null, value: "None" }].concat(
    sortBy(
        Object.keys(AgreementType).map((name) => {
            return { key: name, value: name };
        }),
        "value"
    )
);

const bestExecutionTypes = [{ key: null, value: "None" }].concat(
    sortBy(
        Object.keys(BestExecutionType).map((name) => {
            return { key: name, value: name };
        }),
        "value"
    )
);

const feeItemTypes = [
    { key: null, value: "None" },
    { key: TransactionItemType.Commission, value: "Commission" },
    { key: TransactionItemType.CustodyFee, value: "Custody Fee" },
    { key: TransactionItemType.Fee, value: "Fee" },
    { key: TransactionItemType.ForeignTax, value: "Foreign Tax" },
    { key: TransactionItemType.ManagementFee, value: "Management Fee" },
    { key: TransactionItemType.ManagementCost, value: "Management Cost" },
    { key: TransactionItemType.Rebate, value: "Rebate" }
];

const interestItemTypes = [
    { key: null, value: "None" },
    { key: TransactionItemType.Interest, value: "Interest" },
    { key: TransactionItemType.AccruedInterest, value: "Accrued Interest" }
];

const derivativeTradeInstrumentTypes = [
    InstrumentModelTypeEnum.Swap,
    InstrumentModelTypeEnum.Swaption,
    InstrumentModelTypeEnum.FxSwap,
    InstrumentModelTypeEnum.EquityBasketOption,
    InstrumentModelTypeEnum.EquityUnitOption,
    InstrumentModelTypeEnum.GenericSwap,
    InstrumentModelTypeEnum.GenericFxSwap,
    InstrumentModelTypeEnum.PortfolioSwap
];
const today = new Date().toISOString().slice(0, 10);

type MiniTransactionFormType = Omit<MiniTransaction & { insertSecurity: boolean }, "__typename">;

function resetValuesFunc(type): MiniTransactionFormType {
    return {
        clientId: emptyObjectId,
        _id: null,
        type,
        accountId: emptyObjectId,
        tradeTimestamp: today,
        effectiveDate: null,
        description: null,
        status: TransactionStatus.Pending,
        error: null,
        bestExecutionType: null,
        brokerId: emptyObjectId,
        uniqueTradeId: null,
        brokerTradeId: null,
        source: null,
        instrumentId: null,
        externalAccountId: emptyObjectId,
        currency: CurrencyEnum.SEK,
        valueDate: today,
        attachments: [],
        /* */
        quantity: null,
        price: null,
        amount: null,
        commission: null,
        stampDuty: null,
        rounding: null,
        foreignTax: null,
        dividendPaid: false,
        taxRestitution: false,
        reinvestedDividend: false,
        reinvestedRebate: false,
        fxRate: null,
        insertSecurity: false,
        initialMargin: false,
        loan: false,
        accruedInterest: null,
        agreementType: null,
        clearingHouseId: emptyObjectId,
        clearingBrokerId: emptyObjectId,
        secondInstrumentId: null,
        secondAccountId: emptyObjectId,
        secondAmount: null,
        secondCurrency: CurrencyEnum.SEK,
        bonusShares: null,
        fee: null,
        parentId: null,
        /* */
        settlementAmount: null,
        payableReceivable: null
    };
}

export interface Props {
    id?: string;
    type?: string;
    parentId?: string;
    clientId?: string;
    queryArgMode?: boolean;
    showHeader?: boolean;
}

export function MiniTransactionForm(props: Props): React.ReactElement {
    const previousProps = usePrevious(props);
    const [queryArgs, setQueryArgs] = useSearchParams();
    const copyValues = useRef(null);

    const [values, setValues] = useState(null);

    const id = props.id ? props.id : emptyObjectId;
    const parentId = props.parentId ? props.parentId : null;
    const showHeader = props.showHeader ? true : false;

    const [location, navigate] = useLocation();
    const alertRef = useRef<HTMLElement | null>(null);

    const [{ fetching: getMiniTransactionLoading, error: getMiniTransactionError, data: getMiniTransactionData }, refetch] = useQuery({
        query: GET_MINI_TRANSACTION,
        pause: id === emptyObjectId,
        variables: { id: id }
    });

    const [{ fetching: loading, error: getDataError, data }] = useQuery({
        query: GET_DATA
    });

    const [{ fetching: loadingInstruments, error: getInstrumentsError, data: dataInstruments }] = useQuery({
        query: GET_INSTRUMENTS,
        variables: { filter: { typeIn: [InstrumentTypeEnum.Instrument] } }
    });

    let formType = props.type ? props.type : null;
    let formTypeSource = null;
    if (values && values.type) {
        formTypeSource = values;
    } else if (getMiniTransactionData && getMiniTransactionData.miniTransaction) {
        formTypeSource = getMiniTransactionData.miniTransaction;
    }
    if (formTypeSource) {
        formType = formTypeSource.type;
        if (
            formTypeSource.externalId &&
            formTypeSource.externalId.includes("CLOSE") &&
            formTypeSource.parentId &&
            formTypeSource.parentId !== emptyObjectId
        ) {
            formType = "CloseDerivativeTrade";
        } else if (
            formTypeSource.externalId &&
            formTypeSource.externalId.includes("EXPIRY") &&
            formTypeSource.parentId &&
            formTypeSource.parentId !== emptyObjectId
        ) {
            formType = "ExpiryDerivativeTrade";
        } else if (
            formTypeSource.type === TransactionType.Transfer &&
            formTypeSource.currency &&
            formTypeSource.secondCurrency &&
            formTypeSource.currency !== formTypeSource.secondCurrency
        ) {
            formType = "FxTransfer";
        }
    }
    const needsDerivativeParents = formType === "CloseDerivativeTrade" || formType === "ExpiryDerivativeTrade" || formType === "FxTransfer";
    const needsInsertParents = formType === "CloseDerivativeTrade" || formType === "FxTransfer";

    const [{ data: derivativeParentData }] = useQuery({
        query: GET_DERIVATIVE_TRANSACTIONS,
        pause: !needsDerivativeParents
    });
    const [{ data: insertParentData }] = useQuery({
        query: GET_INSERT_TRANSACTIONS,
        pause: !needsInsertParents
    });

    const [clientId, setClientId] = useState(props.clientId ? props.clientId : null);
    const [client, setClient] = useState({
        _id: null,
        name: "",
        types: [],
        inLedgerTradeDate: null,
        instruments: [],
        accounts: [],
        externalAccounts: []
    });
    const [isLocked, setIsLocked] = useState(false);
    const [isDeleted, setIsDeleted] = useState(false);
    const [modal, setModal] = useState({ showModal: false, payload: null });
    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({
        color: "info",
        visible: false,
        message: ""
    });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });

    const [_, createMiniTransactions] = useMutation(CREATE_MINI_TRANSACTIONS);
    const [__, updateMiniTransaction] = useMutation(UPDATE_MINI_TRANSACTION);

    useAlertTimeOut(alert, setAlert, 5);

    useLayoutEffect(() => {
        setTimeout(() => {
            if (getMiniTransactionData && data && alert && alert.visible && alertRef && alertRef.current) {
                alertRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest"
                });
            }
        }, 400);
    }, [alert, alert.visible, setAlert, alertRef, getMiniTransactionData, data]);

    useEffect(() => {
        if (copyValues && copyValues.current) {
            setValues(copyValues.current);
            copyValues.current = null;
        } else if (stableStringify(props) !== stableStringify(previousProps)) {
            setValues(null);
        } else if (!loading && !getMiniTransactionLoading && !getDataError && !getMiniTransactionError) {
            if (values === null) {
                if (props.id) {
                    const inputValues = cloneDeep(getMiniTransactionData.miniTransaction);

                    if (
                        inputValues.externalId &&
                        inputValues.externalId.includes("CLOSE") &&
                        inputValues.parentId &&
                        inputValues.parentId !== emptyObjectId
                    ) {
                        inputValues.type = "CloseDerivativeTrade";
                    } else if (
                        inputValues.externalId &&
                        inputValues.externalId.includes("EXPIRY") &&
                        inputValues.parentId &&
                        inputValues.parentId !== emptyObjectId
                    ) {
                        inputValues.type = "ExpiryDerivativeTrade";
                    }

                    if (!inputValues.clearingBrokerId) {
                        inputValues.clearingBrokerId = emptyObjectId;
                    }

                    setValues(inputValues);
                    setClientId(getMiniTransactionData.miniTransaction.clientId);
                } else {
                    if (parentId) {
                        const resetValues = resetValuesFunc(props.type);
                        resetValues.parentId = parentId;
                        if (props.clientId) {
                            resetValues.clientId = props.clientId;
                        }
                        setValues(resetValues);
                    } else {
                        const resetValues = resetValuesFunc(props.type);
                        if (props.clientId) {
                            resetValues.clientId = props.clientId;
                        }
                        setValues(resetValues);
                    }
                }
            } else if (
                values.externalId &&
                values.externalId.includes("CLOSE") &&
                values.parentId &&
                values.parentId !== emptyObjectId &&
                values.type !== "CloseDerivativeTrade"
            ) {
                const valuesCloseDerivativeTrade = cloneDeep(values);
                valuesCloseDerivativeTrade.type = "CloseDerivativeTrade";
                setValues(valuesCloseDerivativeTrade);
            } else if (
                values.externalId &&
                values.externalId.includes("EXPIRY") &&
                values.parentId &&
                values.parentId !== emptyObjectId &&
                values.type !== "ExpiryDerivativeTrade"
            ) {
                const valuesExpiryDerivativeTrade = cloneDeep(values);
                valuesExpiryDerivativeTrade.type = "ExpiryDerivativeTrade";
                setValues(valuesExpiryDerivativeTrade);
            } else if (
                values._id &&
                values.type === TransactionType.Transfer &&
                values.currency &&
                values.secondCurrency &&
                values.currency !== values.secondCurrency
            ) {
                const valuesFxTransfer = cloneDeep(values);
                valuesFxTransfer.type = "FxTransfer";
                setValues(valuesFxTransfer);
            } else if (values._id && values.type === TransactionType.Insert) {
                if (!values.insertSecurity && (values.quantity || values.quantity === 0) && (values.price || values.price === 0)) {
                    values.insertSecurity = true;
                    setValues(values);
                }
            }
        }
    }, [
        previousProps,
        props,
        values,
        data,
        clientId,
        parentId,
        getMiniTransactionData,
        getDataError,
        getMiniTransactionError,
        getMiniTransactionLoading,
        loading
    ]);

    // reset accountId on Client change!!!
    useEffect(() => {
        if (data && values) {
            const clientData = data.clients.find((c) => c._id === clientId);
            const v = cloneDeep(values);
            let resetClientSpecifics = false;
            if (clientData && clientData._id && client._id !== clientData._id) {
                setClient(clientData);
            }

            if (clientData && clientData.accounts && clientData.accounts.length > 0) {
                const acc = clientData.accounts.find((a) => a._id === v.accountId);
                if (!acc && v.accountId !== emptyObjectId) {
                    v.accountId = emptyObjectId;
                    resetClientSpecifics = true;
                }
            }
            if (clientData && clientData.externalAccounts && clientData.externalAccounts.length > 0) {
                const acc = clientData.externalAccounts.find((a) => a._id === v.externalAccountId);
                if (!acc && v.externalAccountId !== emptyObjectId) {
                    v.externalAccountId = emptyObjectId;
                    resetClientSpecifics = true;
                }
            }
            if (clientId === emptyObjectId && v.accountId !== emptyObjectId) {
                v.accountId = emptyObjectId; // reset if no client
                resetClientSpecifics = true;
            }
            if (clientId === emptyObjectId && v.externalAccountId !== emptyObjectId) {
                v.externalAccountId = emptyObjectId; // reset if no client
                resetClientSpecifics = true;
            }
            if (resetClientSpecifics) setValues(v);

            if (client && values._id && values.tradeTimestamp && values.status !== TransactionStatus.History) {
                setIsLocked(
                    client.inLedgerTradeDate
                        ? formatYmdInTimeZone(client.inLedgerTradeDate, "Europe/Stockholm") >=
                              formatYmdInTimeZone(values.tradeTimestamp, "Europe/Stockholm")
                        : false
                );
            } else if (!values._id && isLocked) {
                // This is needed after copying a transaction that is locked to make the fields for
                // the new transaction possible to edit
                setIsLocked(false);
            }
        } else if (values === null && id === emptyObjectId) {
            if (parentId) {
                const resetValues = resetValuesFunc(props.type);
                resetValues.parentId = parentId;
                if (props.clientId) {
                    resetValues.clientId = props.clientId;
                }
                setValues(resetValues);
                // ensure deleted state is cleared when starting a new transaction
                if (isDeleted) setIsDeleted(false);
            } else {
                const resetValues = resetValuesFunc(props.type);
                if (props.clientId) {
                    resetValues.clientId = props.clientId;
                }
                setValues(resetValues);
                // ensure deleted state is cleared when starting a new transaction
                if (isDeleted) setIsDeleted(false);
            }
        }

        if (
            values &&
            !isDeleted &&
            values.status === TransactionStatus.Deleted &&
            getMiniTransactionData &&
            getMiniTransactionData.miniTransaction &&
            getMiniTransactionData.miniTransaction.status === TransactionStatus.Deleted
        ) {
            setIsDeleted(true);
        } else if (
            // reset deleted flag when switching to a non-deleted or new transaction
            (isDeleted && values && values.status !== TransactionStatus.Deleted) ||
            (isDeleted && values && !values._id)
        ) {
            setIsDeleted(false);
        }
    }, [client, clientId, data, getMiniTransactionData, id, isDeleted, isLocked, parentId, props, values]);

    if (loading || getMiniTransactionLoading) return <div className="miniform-loading">Loading</div>;

    if (getMiniTransactionError) return <div>{"Error: " + getMiniTransactionError.message}</div>;

    if (getDataError) return <div>{"Error: " + getDataError.message}</div>;

    if (getInstrumentsError) return <div>{"Error: " + getInstrumentsError.message}</div>;

    if (values && !miniTransactionTypes.includes(values.type))
        return <div>{"Error: MiniTransaction is not implemented for type " + values.type}</div>;

    if (values === null) return <div></div>;

    let instruments: ({ _id: string; name: string } & Partial<Instrument>)[] = [];
    if (!loadingInstruments && dataInstruments) {
        instruments = cloneDeep(dataInstruments.instruments);
    }

    let { clients, brokers } = cloneDeep(data);
    brokers = sortBy(brokers, "name");
    const { clearingBrokersUnsorted, clearingHousesUnsorted } = cloneDeep(data);
    const clearingHouses = sortBy(clearingHousesUnsorted, "name");
    const clearingBrokers = sortBy(clearingBrokersUnsorted, "name");
    let derivativeTransactions = [];
    if (derivativeParentData && derivativeParentData.derivativeTransactions) {
        derivativeTransactions = cloneDeep(derivativeParentData.derivativeTransactions);
    }
    let insertTransactions = [];
    if (insertParentData && insertParentData.insertTransactions) {
        insertTransactions = cloneDeep(insertParentData.insertTransactions);
    }

    derivativeTransactions.forEach((transaction) => {
        transaction["name"] = transaction.name ? transaction["name"] : transaction["externalId"];
        delete transaction["externalId"];
    });

    const insertDerivativeTransactions = [];
    insertTransactions.forEach((transaction) => {
        transaction["name"] = transaction.name ? transaction["name"] : transaction["externalId"];
        delete transaction["externalId"];
        if (
            transaction.quantity &&
            transaction.instrument &&
            transaction.instrument.model &&
            derivativeTradeInstrumentTypes.includes(transaction.instrument.model._t)
        ) {
            insertDerivativeTransactions.push(transaction);
        }
    });

    const derivativeAndInsertDerivativeTransactions = derivativeTransactions
        .concat(insertDerivativeTransactions)
        .concat({ _id: emptyObjectId, name: "No parent transaction", source: "", __typename: "MiniTransaction" });

    // Only implemented for fxSwap atm
    const expiryDerivativeTransactions = derivativeTransactions.filter(
        (transaction) =>
            transaction.instrument &&
            transaction.instrument.model &&
            transaction.instrument.model._t === InstrumentModelTypeEnum.FxSwap &&
            transaction.type === TransactionType.DerivativeTrade
    );

    //Filter instruments options for FundTrade
    if (values && values.type === TransactionType.FundTrade) {
        instruments = instruments.reduce(function (filtered, item) {
            if (item.productType === InstrumentProductTypeEnum.Fund) filtered.push(item);

            return filtered;
        }, []);
    }

    // clearingHouseId
    const clearingHouse = clearingHouses.find((clearingHouse) => clearingHouse._id === null);
    if (!clearingHouse) {
        clearingHouses.unshift({ _id: emptyObjectId, name: "No clearing house" });
    } else if (clearingHouse.name.match("clearing")) {
        clearingHouse.name = "No clearing house";
    }

    // clearingBrokerId
    const clearingBroker = clearingBrokers.find((clearingBroker) => clearingBroker._id === null);
    if (!clearingBroker) {
        clearingBrokers.unshift({ _id: emptyObjectId, name: "No clearing broker" });
    } else if (clearingBroker.name.match("clearing")) {
        clearingBroker.name = "No clearing house";
    }

    // brokerId
    if (!brokers.find((d) => d._id === null)) {
        brokers.unshift({ _id: emptyObjectId, name: "No broker" });
    } else if (
        values.type &&
        (values.type === TransactionType.Collateral ||
            values.type === TransactionType.BondTrade ||
            values.type === TransactionType.DerivativeTrade)
    ) {
        // making sure No broker alternative is removed from brokers list,
        //otherwise issue with alternative remaining after choosing ex. stockTrade
        if (values.type === TransactionType.Collateral) {
            clearingBrokers.unshift({ _id: emptyObjectId, name: "No clearing broker" });
            // TODO: Josefine, remove the line below and fix the code
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            brokers === brokers.push(clearingBrokers);
        }
        brokers = brokers.filter(function (obj) {
            return obj._id !== null;
        });
        // default broker is first one
        if (values.brokerId === emptyObjectId) {
            values.brokerId = brokers[0]._id;
        }
    }

    // Fee itemType - default first one
    if (values.type === TransactionType.Fee && !values.itemType) {
        values.itemType = feeItemTypes[0].key;
    }

    // Interest itemType - default first one
    if (values.type === TransactionType.Interest && !values.itemType) {
        values.itemType = interestItemTypes[0].key;
    }

    clients = sortBy(clients, "name");

    const instrumentsById = keyBy(instruments, "_id");

    const currencies = Object.values(CurrencyEnum).sort();

    let partyInstrumentsById: Record<string, any> = {};

    if (client && client.instruments) {
        if (values.type === TransactionType.Transfer || values.type === "FxTransfer") {
            instruments = [];
        }
        const instrumentsByCurrency: Record<string, Instrument[]> = {};
        if (
            values.type &&
            !values.type.endsWith("Trade") &&
            values.type !== TransactionType.Insert &&
            values.itemType !== TransactionItemType.AccruedInterest
        ) {
            const instrumentsByName = keyBy(instruments, "name");
            client.instruments.forEach((instrument) => {
                instrumentsByName[instrument.name] = instrument;
                instrumentsByCurrency[instrument.currency] = instrumentsByCurrency[instrument.currency]
                    ? instrumentsByCurrency[instrument.currency].concat([instrument as Instrument])
                    : [instrument as Instrument];
            });
            instruments = Object.values(instrumentsByName);
        }
        // Transfer can only transfer between same currencies, restricting which instruments are valid options
        if (values.type === TransactionType.Transfer && values.instrumentId && values.currency && !values.secondInstrumentId) {
            instruments = instrumentsByCurrency[values.currency] as ({ _id: string; name: string } & Partial<Instrument>)[];
        } else if (values.type === TransactionType.Transfer && values.secondInstrumentId && values.secondCurrency && !values.instrumentId) {
            instruments = instrumentsByCurrency[values.secondCurrency] as ({ _id: string; name: string } & Partial<Instrument>)[];
        }

        partyInstrumentsById = keyBy(client.instruments, "_id");
    }

    //Change instruments options for Insert cash
    if (values && values.type === TransactionType.Insert && !values.insertSecurity) {
        instruments = Object.values(partyInstrumentsById);
    }

    // accountId + externalAccountId
    let accounts = [{ _id: emptyObjectId, name: "Select account", description: "" }];
    let externalAccounts = [{ _id: emptyObjectId, name: "Select external account" }];
    if (client) {
        const clientAccounts = client.accounts.reduce(function (filtered, item) {
            if (item.type === PartyAccountType.Physical) {
                filtered.push({ _id: item._id, name: item.name, description: item.description });
            }

            return filtered;
        }, []);
        accounts = accounts.concat(clientAccounts);

        const clientExternalAccounts = client.externalAccounts.reduce(function (filtered, item) {
            filtered.push({ _id: item._id, name: item.name });

            return filtered;
        }, []);
        externalAccounts = externalAccounts.concat(clientExternalAccounts);
    }

    const sources = [{ key: null, value: "None" }].concat(
        sortBy(
            Object.keys(SourceType).map((item) => {
                return { key: item, value: item };
            }),
            "value"
        )
    );

    return (
        <div>
            {modal.showModal ? (
                <YesNoModal
                    warningText={"Are you sure you want to delete transaction with id " + modal.payload._id + "?"}
                    modal={{
                        showModal: modal.showModal,
                        payload: modal.payload
                    }}
                    setModal={setModal}
                    onYes={() => {
                        let mutationData: any;
                        updateMiniTransaction({ input: modal.payload })
                            .then((result) => {
                                if ("error" in result && result.error) {
                                    const message = formikUrqlErrorFormater(result.error);
                                    setAlert({ color: "danger", visible: true, message });
                                } else {
                                    mutationData = result.data.updateMiniTransaction;
                                    setAlert({
                                        color: "success",
                                        visible: true,
                                        message: `The transaction '${mutationData._id}' was updated and deleted successfully!`
                                    });
                                }
                                return true;
                            })
                            .catch((error) => {
                                setAlert({ color: "danger", visible: true, message: error.toString() });
                            })
                            .finally(() => {
                                if (mutationData && mutationData._id) {
                                    setValues(mutationData);
                                    setClientId(mutationData.clientId);
                                    refetch();
                                }
                            });
                    }}
                />
            ) : null}

            {showHeader ? <h2 className="mtf-header">{startCase(values.type)}</h2> : null}

            <div id="minitransactionform" className="minitransactionform form">
                <Formik
                    enableReinitialize={true}
                    validateOnMount={true}
                    initialValues={values}
                    validate={(validateValues) => {
                        const { type } = values;
                        const noExternalId = noExternalIdTypes.findIndex((d) => d === type) >= 0;
                        const tradeType = type && type.endsWith("Trade");

                        if (!values.clientId) {
                            values.clientId = clientId;
                        } else {
                            setClientId(values.clientId);
                        }

                        if (
                            values.type !== "CloseDerivativeTrade" &&
                            values.type !== "ExpiryDerivativeTrade" &&
                            validateValues.clientId &&
                            values.clientId.toString() !== validateValues.clientId.toString()
                        ) {
                            setClientId(validateValues.clientId);
                        }

                        // Insert security
                        if (
                            (validateValues.quantity || validateValues.quantity === 0) &&
                            (validateValues.price || validateValues.price === 0) &&
                            validateValues.type === TransactionType.Insert &&
                            !validateValues.insertSecurity
                        ) {
                            validateValues.insertSecurity = true;
                            setValues(validateValues);
                            refetch();
                        }

                        //  close derivative trade
                        if (
                            validateValues.externalId &&
                            validateValues.externalId.includes("CLOSE") &&
                            validateValues.parentId &&
                            validateValues.parentId !== emptyObjectId
                        ) {
                            validateValues.type = "CloseDerivativeTrade";
                            setValues(validateValues);
                        }

                        // expiry derivative trade
                        if (
                            validateValues.externalId &&
                            validateValues.externalId.includes("EXPIRY") &&
                            validateValues.parentId &&
                            validateValues.parentId !== emptyObjectId
                        ) {
                            validateValues.type = "ExpiryDerivativeTrade";
                            setValues(validateValues);
                        }

                        //Insert derivative trade
                        if (
                            validateValues.bestExecutionType === "Maintenance" &&
                            validateValues.source === "Internal" &&
                            validateValues.type === TransactionType.Insert &&
                            validateValues.brokerId &&
                            validateValues.brokerId !== emptyObjectId &&
                            validateValues.agreementType &&
                            (!validateValues.parentId || validateValues.parentId === emptyObjectId)
                        ) {
                            validateValues.insert = true;
                            validateValues.type = TransactionType.DerivativeTrade;
                            setValues(validateValues);
                        }

                        if (!validateValues.brokerId) {
                            validateValues.brokerId = brokers.length > 0 ? brokers[0]._id : null;
                            setValues(validateValues);
                        }

                        // Filter accounts+externalAccounts based on client
                        if (!validateValues.accountId) {
                            validateValues.accountId = accounts.length > 0 ? accounts[0]._id : null;
                            setValues(validateValues);
                        }
                        if (!validateValues.externalAccountId) {
                            validateValues.externalAccountId = externalAccounts.length > 0 ? externalAccounts[0]._id : null;
                            setValues(validateValues);
                        }

                        if (!validateValues.itemType && validateValues.type === TransactionType.Fee) {
                            validateValues.itemType = feeItemTypes[0].key;
                            setValues(validateValues);
                        }

                        if (!validateValues.itemType && validateValues.type === TransactionType.Interest) {
                            validateValues.itemType = interestItemTypes[0].key;
                            setValues(validateValues);
                        }

                        /*if (!validateValues.instrumentId) {
                        validateValues.instrumentId = instruments.length > 0 ? instruments[0]._id : null;
                        setValues(validateValues);
                        }

                        if (!validateValues.secondInstrumentId && validateValues.type === TransactionType.Transfer) {
                            validateValues.secondInstrumentId = instruments.length > 0 ? instruments[0]._id : null;
                            setValues(validateValues);
                        }*/

                        if (!validateValues.agreementType) {
                            validateValues.agreementType = agreementTypes.length > 0 ? agreementTypes[0].key : null;
                            setValues(validateValues);
                        }

                        if (!validateValues.currency) {
                            if (validateValues.instrumentId in instrumentsById) {
                                validateValues.currency = instrumentsById[validateValues.instrumentId].currency;
                            } else if (validateValues.instrumentId in partyInstrumentsById) {
                                validateValues.currency = partyInstrumentsById[validateValues.instrumentId].currency;
                            } else {
                                validateValues.currency = currencies[0];
                            }
                            setValues(validateValues);
                        }

                        if (!validateValues.secondCurrency && type === "FxTransfer") {
                            if (validateValues.secondInstrumentId in partyInstrumentsById) {
                                validateValues.secondCurrency = partyInstrumentsById[validateValues.secondInstrumentId].currency;
                            } else {
                                validateValues.secondCurrency = currencies[0];
                            }
                            setValues(validateValues);
                        }

                        /*if (!validateValues.parentId && validateValues.type === "CloseDerivativeTrade") {
                            validateValues.parentId =
                                derivativeAndInsertDerivativeTransactions.length > 0 ? derivativeAndInsertDerivativeTransactions[0]._id : null;
                        }*/
                        if (
                            validateValues.clientId !== values.clientId ||
                            validateValues.currency !== values.currency ||
                            validateValues.instrumentId !== values.instrumentId ||
                            ((type === "FxTransfer" || type === TransactionType.Transfer) &&
                                validateValues.secondInstrumentId !== values.secondInstrumentId) ||
                            ((type === "FxTransfer" || type === TransactionType.Transfer) &&
                                validateValues.secondCurrency !== values.secondCurrency)
                        ) {
                            if (values.instrumentId !== validateValues.instrumentId) {
                                if (validateValues.instrumentId in partyInstrumentsById) {
                                    validateValues.currency = partyInstrumentsById[validateValues.instrumentId].currency;
                                }
                                if (validateValues.instrumentId in instrumentsById) {
                                    validateValues.currency = instrumentsById[validateValues.instrumentId].currency;
                                }
                            }
                            if (
                                values.secondInstrumentId !== validateValues.secondInstrumentId &&
                                (type === "FxTransfer" || type === TransactionType.Transfer)
                            ) {
                                if (validateValues.secondInstrumentId in partyInstrumentsById) {
                                    validateValues.secondCurrency = partyInstrumentsById[validateValues.secondInstrumentId].currency;
                                }
                                if (validateValues.secondInstrumentId in instrumentsById) {
                                    validateValues.secondCurrency = instrumentsById[validateValues.secondInstrumentId].currency;
                                }
                            }
                            setValues(validateValues);
                        }

                        //Revert transaction (creates one revert transaction and one new transaction), only Dividend + Interest atm
                        if (validateValues.revertTransaction) {
                            if (!validateValues.newValueDate) {
                                validateValues.newValueDate = validateValues.valueDate;
                            }
                            if (!validateValues.newStatus) {
                                validateValues.newStatus = TransactionStatus.Pending;
                            }
                            if (!validateValues.newCurrency) {
                                validateValues.newCurrency = validateValues.currency;
                            }

                            setValues(validateValues);
                        }

                        const errors: any = {};
                        if (
                            tradeType &&
                            type !== "CloseDerivativeTrade" &&
                            type !== "ExpiryDerivativeTrade" &&
                            type !== TransactionType.FundTrade
                        ) {
                            ["price", "quantity"].forEach((d) => {
                                if (typeof validateValues[d] !== "number") {
                                    errors[d] = "Required";
                                }
                            });
                            if (validateValues.brokerId === null && type !== TransactionType.StockTrade) {
                                errors.brokerId = "Required";
                            }
                        }
                        if (type === TransactionType.Insert && validateValues.insertSecurity) {
                            if (typeof validateValues.quantity !== "number") {
                                errors.quantity = "Required";
                            }
                            if (validateValues.brokerId === null && type !== TransactionType.StockTrade) {
                                errors.brokerId = "Required";
                            }
                        }
                        let requiredFields = ["tradeTimestamp", "valueDate"];

                        if (type !== "CloseDerivativeTrade" && type !== "ExpiryDerivativeTrade") {
                            requiredFields.push("currency", "clientId");
                        }
                        if (
                            (type !== "CloseDerivativeTrade" &&
                                type !== "ExpiryDerivativeTrade" &&
                                type !== TransactionType.FundTrade &&
                                type !== TransactionType.Insert) ||
                            (type === TransactionType.Insert && values.insertSecurity)
                        ) {
                            requiredFields.push("accountId");
                        }

                        if (!noExternalId && !validateValues.insert) {
                            requiredFields.push("externalId");
                            requiredFields.push("source");
                        }

                        const regExpExternalId = new RegExp("[^A-ZÅÄÖ0-9-_.]");
                        if (
                            validateValues.externalId &&
                            regExpExternalId.test(validateValues.externalId) &&
                            validateValues.status !== TransactionStatus.Deleted
                        ) {
                            errors.externalId = "Only valid characters: capital A-Ö, numbers 0-9 and three special characters .-_";
                        }

                        if (type !== "CloseDerivativeTrade" && type !== "ExpiryDerivativeTrade") {
                            requiredFields.push("instrumentId");
                        }

                        if (type === TransactionType.Fee || type === TransactionType.Interest) {
                            requiredFields.push("itemType");
                        }

                        if (type === "CloseDerivativeTrade" || type === "ExpiryDerivativeTrade") {
                            requiredFields.push("parentId");
                        }

                        if (type === TransactionType.FundTrade) {
                            requiredFields.push("quantity");
                        }

                        if (type === TransactionType.BondTrade || type === TransactionType.Collateral) {
                            requiredFields.push("brokerId");
                        }

                        if (type === TransactionType.DerivativeTrade) {
                            requiredFields.push("uniqueTradeId");
                            requiredFields.push("agreementType");
                        }

                        if (type === TransactionType.Transfer) {
                            requiredFields.push("secondInstrumentId");
                            requiredFields.push("amount");
                        }

                        if (type === TransactionType.Collateral) {
                            requiredFields.push("quantity");
                            requiredFields.push("agreementType");
                        }

                        if (
                            ((tradeType && !validateValues.insert) || type === TransactionType.Transfer || type === "FxTransfer") &&
                            type !== "CloseDerivativeTrade" &&
                            type !== "ExpiryDerivativeTrade" &&
                            type !== TransactionType.FundTrade
                        ) {
                            requiredFields.push("bestExecutionType");
                        }

                        if (
                            (!tradeType || type === "CloseDerivativeTrade") &&
                            type !== TransactionType.Collateral &&
                            type !== TransactionType.Insert
                        ) {
                            requiredFields.push("amount");
                        }

                        if (type === "FxTransfer") {
                            requiredFields.push("secondCurrency");
                            requiredFields.push("secondInstrumentId");
                            requiredFields.push("secondAmount");
                        }

                        if ((type === TransactionType.Dividend || type === TransactionType.Interest) && validateValues.revertTransaction) {
                            requiredFields = requiredFields.concat(["newValueDate", "newCurrency", "newAmount"]);
                        }

                        requiredFields.forEach((d) => {
                            if (
                                (!validateValues[d] && validateValues[d] !== 0) ||
                                (validateValues[d] && validateValues[d] === emptyObjectId) ||
                                validateValues[d] === "None"
                            ) {
                                errors[d] = "Required";
                            }
                        });

                        return Object.keys(errors).length > 0 ? errors : {};
                    }}
                    onSubmit={(submitValues, { setSubmitting, setErrors }) => {
                        let input = { ...submitValues };
                        if (input.valueDate instanceof Date) {
                            input.valueDate = input.valueDate.toISOString().slice(0, 10);
                        }
                        ["brokerId", "clearingHouseId", "clearingBrokerId"].forEach((d) => {
                            if (input[d] === "") {
                                input[d] = null;
                            }
                        });
                        delete input.brokerTransaction;
                        delete input.corporateAction;
                        delete input.settlementAmount;
                        delete input.payableReceivable;
                        delete input.updateTimestamp;
                        if (input.type === "CloseDerivativeTrade" && !input.insert) {
                            input.close = true;
                            input.type = TransactionType.DerivativeTrade;
                        } else if (input.type === "CloseDerivativeTrade" && input.insert) {
                            input.close = true;
                            input.type = TransactionType.Insert;
                        } else if (input.type === "ExpiryDerivativeTrade") {
                            input.expiry = true;
                            input.type = TransactionType.DerivativeTrade;
                        } else if (input.type === TransactionType.DerivativeTrade && input.insert) {
                            delete input.parentId;
                            input.type = TransactionType.Insert;
                        }

                        delete input.insert;
                        if (!input.insertSecurity && input.type === TransactionType.Insert) {
                            delete input.quantity;
                            delete input.price;
                        }
                        delete input.insertSecurity;

                        if (input.type === TransactionType.Transfer) {
                            input.secondCurrency = input.currency;
                            input.secondAmount = -input.amount;
                        }

                        if (input.type === "FxTransfer") {
                            input.type = TransactionType.Transfer;
                        }

                        // Only applies to transfer and only relevant if set to id other than default emptyObjectId
                        if (
                            ((input.type === TransactionType.Transfer || input.type === "FxTransfer") &&
                                input.secondAccountId === emptyObjectId) ||
                            (input.type !== TransactionType.Transfer && input.type !== "FxTransfer")
                        ) {
                            delete input.secondAccountId;
                        }

                        //Revert transaction (creates one revert transaction and one new transaction), only Dividend + Interest atm
                        if (input.revertTransaction) {
                            input.amount = input.newAmount;
                            input.status = input.newStatus;
                            input.tradeTimestamp = today;
                            input.valueDate = input.newValueDate;
                            input.currency = input.newCurrency;
                            input.foreignTax = input.newForeignTax;
                            input.rounding = input.newRounding;
                            input.parentId = input._id;
                            input._id = null;
                        }
                        delete input.newAmount;
                        delete input.newStatus;
                        delete input.newValueDate;
                        delete input.newCurrency;
                        delete input.newForeignTax;
                        delete input.newRounding;

                        if (input._id !== null) {
                            delete input.type;
                            delete input.source;
                            delete input.externalId;
                            delete input.clientId;
                            delete input.revertTransaction;
                            input = recursivelyRemoveKey(input, "__typename");
                            if (input.status && input.status === TransactionStatus.Deleted) {
                                setModal({ showModal: true, payload: input });
                            } else {
                                let mutationData: any;
                                updateMiniTransaction({ input })
                                    .then((result) => {
                                        if ("error" in result && result.error) {
                                            const message = formikUrqlErrorFormater(result.error, setErrors);
                                            setAlert({ color: "danger", visible: true, message });
                                        } else {
                                            mutationData = result.data.updateMiniTransaction;

                                            setAlert({
                                                color: "success",
                                                visible: true,
                                                message: `The transaction '${mutationData._id}' was updated successfully!`
                                            });
                                        }
                                        return true;
                                    })
                                    .catch((error) => {
                                        setAlert({ color: "danger", visible: true, message: error.toString() });
                                    })
                                    .finally(() => {
                                        if (mutationData && !mutationData.clearingBrokerId) {
                                            mutationData.clearingBrokerId = emptyObjectId;
                                        }
                                        if (mutationData && mutationData._id) {
                                            //  close derivative trade
                                            if (
                                                mutationData.externalId &&
                                                mutationData.externalId.includes("CLOSE") &&
                                                mutationData.parentId &&
                                                mutationData.parentId !== emptyObjectId
                                            ) {
                                                mutationData.type = "CloseDerivativeTrade";
                                            }
                                            // expiry derivative trade
                                            if (
                                                mutationData.externalId &&
                                                mutationData.externalId.includes("EXPIRY") &&
                                                mutationData.parentId &&
                                                mutationData.parentId !== emptyObjectId
                                            ) {
                                                mutationData.type = "ExpiryDerivativeTrade";
                                            }

                                            //Insert derivative trade
                                            if (
                                                mutationData.bestExecutionType === "Maintenance" &&
                                                mutationData.source === "Internal" &&
                                                mutationData.type === TransactionType.Insert &&
                                                mutationData.brokerId &&
                                                mutationData.brokerId !== emptyObjectId &&
                                                mutationData.agreementType &&
                                                (!mutationData.parentId || mutationData.parentId === emptyObjectId)
                                            ) {
                                                mutationData.insert = true;
                                                mutationData.type = TransactionType.DerivativeTrade;
                                            }

                                            setValues(mutationData);
                                            setClientId(mutationData.clientId);
                                            refetch();
                                        }
                                        setSubmitting(false);
                                    });
                            }
                        } else {
                            delete input._id;
                            delete input.effectiveDate;
                            delete input.brokerTradeId;
                            delete input.error;

                            input = recursivelyRemoveKey(input, "__typename");

                            let mutationData: any;
                            createMiniTransactions({ input: [input] })
                                .then((result) => {
                                    if ("error" in result && result.error) {
                                        const message = formikUrqlErrorFormater(result.error, setErrors);
                                        setAlert({ color: "danger", visible: true, message });
                                    } else {
                                        mutationData = result.data.createMiniTransactions[0];
                                        setAlert({
                                            color: "success",
                                            visible: true,
                                            message: `The transaction(s) '${mutationData._id}' was(were) created successfully!`
                                        });
                                    }
                                    return true;
                                })
                                .catch((error) => {
                                    setAlert({ color: "danger", visible: true, message: error.toString() });
                                })
                                .finally(() => {
                                    if (mutationData && mutationData._id) {
                                        const pathname = location.split("?")[0].split("#")[0];
                                        const path = pathname.split("/");
                                        path.pop();
                                        // Using for closing positions on portfoliomanager->positions page, switching between
                                        // closing a derivativeTrade and showing the new closeDerivativeTrade created. Applies for
                                        // expiry as well
                                        if (props.queryArgMode) {
                                            setQueryArgs((prev) => {
                                                prev.set("id", mutationData._id);
                                                return prev;
                                            });
                                        } else {
                                            path.push(mutationData._id + (queryArgs.toString() ? `?${queryArgs.toString()}` : ""));
                                            navigate(path.join("/"), { replace: true });
                                        }

                                        if (!mutationData.clearingBrokerId) {
                                            mutationData.clearingBrokerId = emptyObjectId;
                                        }

                                        //  Close derivative trade
                                        if (
                                            mutationData.externalId &&
                                            mutationData.externalId.includes("CLOSE") &&
                                            mutationData.parentId &&
                                            mutationData.parentId !== emptyObjectId
                                        ) {
                                            mutationData.type = "CloseDerivativeTrade";
                                        }
                                        // expiry derivative trade
                                        if (
                                            mutationData.externalId &&
                                            mutationData.externalId.includes("EXPIRY") &&
                                            mutationData.parentId &&
                                            mutationData.parentId !== emptyObjectId
                                        ) {
                                            mutationData.type = "ExpiryDerivativeTrade";
                                        }

                                        //Insert derivative trade
                                        if (
                                            mutationData.bestExecutionType === "Maintenance" &&
                                            mutationData.source === "Internal" &&
                                            mutationData.type === TransactionType.Insert &&
                                            mutationData.brokerId &&
                                            mutationData.brokerId !== emptyObjectId &&
                                            mutationData.agreementType &&
                                            (!mutationData.parentId || mutationData.parentId === emptyObjectId)
                                        ) {
                                            mutationData.insert = true;
                                            mutationData.type = TransactionType.DerivativeTrade;
                                        }
                                        setValues(mutationData);
                                        refetch();
                                    }

                                    setSubmitting(false);
                                });
                        }

                        //setSubmitting(false);
                    }}
                >
                    {({ isSubmitting, values, errors }) => {
                        const { type } = values;
                        const noExternalId = noExternalIdTypes.findIndex((d) => d === type) >= 0;
                        const tradeType = type && type.endsWith("Trade");

                        return (
                            <Fragment>
                                <div className="flex flex-wrap -mx-2">
                                    {isLocked && !values.revertTransaction ? (
                                        <div>
                                            <i style={{ color: "red" }}>{"Restrictions apply due to locked accounting"}</i>
                                        </div>
                                    ) : null}
                                    {type === "ExpiryDerivativeTrade" ? (
                                        <div>
                                            <i style={{ color: "red" }}>{"Expiry derivative trade is only implemented for fxSwap"}</i>
                                        </div>
                                    ) : null}
                                </div>
                                {values._id && values.type !== TransactionType.CreateRedeem && !values.revertTransaction && !isDeleted ? (
                                    //Not possible to create new createRedeems, only view and update

                                    <CopyMiniTransactionButton
                                        queryArgMode={props.queryArgMode}
                                        type={values.type}
                                        values={values}
                                        copyValues={copyValues}
                                    />
                                ) : null}
                                {isDeleted ? (
                                    <div>
                                        <i style={{ color: "red" }}>{"Transaction is deleted."}</i>
                                    </div>
                                ) : null}

                                <div>
                                    <Form autoComplete="off">
                                        <div className="flex flex-wrap -mx-2">
                                            {type !== "CloseDerivativeTrade" && type !== "ExpiryDerivativeTrade" ? (
                                                <SelectField
                                                    name="clientId"
                                                    label={<Link to={"/parties/" + values.clientId}>Client*</Link>}
                                                    options={[
                                                        { key: emptyObjectId, value: "Select client" },
                                                        ...clients
                                                            .map((d) => {
                                                                if (values && (values.type === TransactionType.Insert || values.insert)) {
                                                                    if (!d.types.includes(PartyType.Fund)) {
                                                                        return { key: d._id, value: d.name };
                                                                    }
                                                                } else {
                                                                    return { key: d._id, value: d.name };
                                                                }
                                                            })
                                                            .filter((client) => client)
                                                    ]}
                                                    disabled={values._id ? true : isSubmitting || isDeleted}
                                                    className=""
                                                />
                                            ) : null}
                                            {(type !== "CloseDerivativeTrade" &&
                                                type !== "ExpiryDerivativeTrade" &&
                                                type !== TransactionType.Insert) ||
                                            (type === TransactionType.Insert && values.insertSecurity) ? (
                                                <SelectField
                                                    key={`acc-${clientId}`}
                                                    name="accountId"
                                                    label={
                                                        values.clientId !== emptyObjectId && values.accountId !== emptyObjectId ? (
                                                            <Link to={"/parties/" + values.clientId + "/accounts/" + values.accountId}>
                                                                {type === TransactionType.FundTrade ? "Account" : "Account*"}
                                                            </Link>
                                                        ) : (
                                                            <div>{type === TransactionType.FundTrade ? "Account" : "Account*"}</div>
                                                        )
                                                    }
                                                    options={accounts.map((d) => ({ key: d._id, value: `${d.name} ${d.description}` }))}
                                                    disabled={
                                                        isSubmitting ||
                                                        isLocked ||
                                                        !values.clientId ||
                                                        values.clientId === emptyObjectId ||
                                                        values.type === TransactionType.CreateRedeem ||
                                                        isDeleted
                                                    }
                                                    className=""
                                                />
                                            ) : null}
                                            {(type === "CloseDerivativeTrade" && !values._id) ||
                                            (type === TransactionType.DerivativeTrade && !values._id) ? (
                                                <CheckBoxField
                                                    name="insert"
                                                    label="Insert*"
                                                    disabled={isSubmitting || isLocked || isDeleted}
                                                    className=""
                                                />
                                            ) : null}
                                            {type !== "ExpiryDerivativeTrade" ? (
                                                <TimestampField
                                                    name="tradeTimestamp"
                                                    label="Trade timestamp*"
                                                    disabled={
                                                        isSubmitting ||
                                                        isLocked ||
                                                        values.type === TransactionType.CreateRedeem ||
                                                        isDeleted
                                                    }
                                                    className=""
                                                />
                                            ) : null}
                                            {(tradeType &&
                                                type !== TransactionType.StockTrade &&
                                                type !== TransactionType.FundTrade &&
                                                type !== "CloseDerivativeTrade" &&
                                                type !== "ExpiryDerivativeTrade") ||
                                            type === TransactionType.Collateral ||
                                            type === TransactionType.Fee ||
                                            type === TransactionType.Interest ? (
                                                <SelectField
                                                    name="brokerId"
                                                    label={
                                                        <Link to={"/parties/" + values.brokerId}>
                                                            {type === TransactionType.Collateral ? "Counterparty*" : "Broker*"}
                                                        </Link>
                                                    }
                                                    options={brokers.map((d) => ({ key: d._id, value: d.name }))}
                                                    disabled={isSubmitting || isLocked || isDeleted}
                                                    className=""
                                                />
                                            ) : null}
                                            {type === TransactionType.StockTrade ? (
                                                <SelectField
                                                    name="brokerId"
                                                    label={<Link to={"/parties/" + values.brokerId}>Broker</Link>}
                                                    options={brokers.map((d) => ({ key: d._id, value: d.name }))}
                                                    disabled={isSubmitting || isLocked || isDeleted}
                                                    className=""
                                                />
                                            ) : null}
                                            {type === TransactionType.Transfer || type === "FxTransfer" ? (
                                                <SelectField
                                                    name="brokerId"
                                                    label={<Link to={"/parties/" + values.brokerId}>Broker</Link>}
                                                    options={brokers.map((d) => ({ key: d._id, value: d.name }))}
                                                    disabled={isSubmitting || isLocked || isDeleted}
                                                    className=""
                                                />
                                            ) : null}
                                            {(type === TransactionType.Transfer ||
                                                type === "FxTransfer" ||
                                                (tradeType &&
                                                    type !== "CloseDerivativeTrade" &&
                                                    type !== "ExpiryDerivativeTrade" &&
                                                    type !== TransactionType.FundTrade)) &&
                                            !values.insert ? (
                                                <SelectField
                                                    name="bestExecutionType"
                                                    label="Best execution*"
                                                    options={bestExecutionTypes}
                                                    tooltips={BestExecutionTypeDescriptions}
                                                    disabled={isSubmitting || isDeleted}
                                                    className=""
                                                />
                                            ) : null}
                                            {type !== TransactionType.Transfer &&
                                            type !== "FxTransfer" &&
                                            !tradeType &&
                                            type !== TransactionType.Collateral ? (
                                                <SelectField
                                                    name="currency"
                                                    label="Currency*"
                                                    options={currencies}
                                                    disabled={
                                                        isSubmitting ||
                                                        isLocked ||
                                                        values.type === TransactionType.CreateRedeem ||
                                                        isDeleted
                                                    }
                                                    className=""
                                                />
                                            ) : null}
                                            {type !== "ExpiryDerivativeTrade" ? (
                                                <DateField
                                                    name="valueDate"
                                                    label="Value date*"
                                                    disabled={isSubmitting || values.revertTransaction || isDeleted}
                                                    className=""
                                                />
                                            ) : null}
                                            {type === TransactionType.Transfer || type === "FxTransfer" ? (
                                                <NullableDateField
                                                    name="carryOwnCostDate"
                                                    label="Carry own cost date"
                                                    disabled={isSubmitting || isLocked || isDeleted}
                                                    className=""
                                                />
                                            ) : null}
                                            {type !== TransactionType.Transfer &&
                                            type !== "FxTransfer" &&
                                            type !== "CloseDerivativeTrade" &&
                                            type !== "ExpiryDerivativeTrade" ? (
                                                <SearchListField
                                                    name="instrumentId"
                                                    label={
                                                        values.instrumentId in partyInstrumentsById ? (
                                                            <Link
                                                                to={"/parties/" + values.clientId + "/instruments/" + values.instrumentId}
                                                            >
                                                                PartyInstrument*
                                                            </Link>
                                                        ) : (
                                                            <Link to={"/instruments/" + values.instrumentId}>Instrument*</Link>
                                                        )
                                                    }
                                                    options={instruments}
                                                    disabled={
                                                        isSubmitting ||
                                                        isLocked ||
                                                        values.type === TransactionType.CreateRedeem ||
                                                        isDeleted
                                                    }
                                                    className=""
                                                />
                                            ) : null}

                                            {tradeType &&
                                            type !== "CloseDerivativeTrade" &&
                                            type !== "ExpiryDerivativeTrade" &&
                                            type !== TransactionType.FundTrade ? (
                                                <div className="flex flex-grow w-full">
                                                    <SelectField
                                                        name="currency"
                                                        label="Currency*"
                                                        options={currencies}
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        className=""
                                                    />
                                                    <NumberField
                                                        name="quantity"
                                                        label="Quantity*"
                                                        className="ml-1"
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        changeTolerancePercent={values && values._id ? defaultFatFingerTolerance : null}
                                                    />
                                                    <NumberField
                                                        name="price"
                                                        label="Price*"
                                                        className="ml-1"
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        changeTolerancePercent={values && values._id ? defaultFatFingerTolerance : null}
                                                    />
                                                </div>
                                            ) : null}
                                            {type === TransactionType.StockTrade ? (
                                                <div className="mb-4 w-full">
                                                    <div className="flex flex-grow w-full">
                                                        <NumberField
                                                            name="commission"
                                                            label="Commission"
                                                            disabled={isSubmitting || isLocked || isDeleted}
                                                            changeTolerancePercent={values && values._id ? defaultFatFingerTolerance : null}
                                                            className=""
                                                        />
                                                        <NumberField
                                                            name="stampDuty"
                                                            label="Stamp duty"
                                                            className="ml-1"
                                                            disabled={isSubmitting || isLocked || isDeleted}
                                                        />
                                                        <NumberField
                                                            name="rounding"
                                                            label="Rounding"
                                                            className="ml-1"
                                                            disabled={isSubmitting || isLocked || isDeleted}
                                                        />
                                                    </div>
                                                    <NullableDateField
                                                        name="carryOwnCostDate"
                                                        label="Carry own cost date"
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        className=""
                                                    />
                                                </div>
                                            ) : type === TransactionType.BondTrade ? (
                                                <Fragment>
                                                    <NumberField
                                                        name="accruedInterest"
                                                        label="Accrued interest"
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        changeTolerancePercent={values && values._id ? defaultFatFingerTolerance : null}
                                                        className=""
                                                    />
                                                    <NumberField
                                                        name="rounding"
                                                        label="Rounding"
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        className=""
                                                    />

                                                    <NullableDateField
                                                        name="carryOwnCostDate"
                                                        label="Carry own cost date"
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        className=""
                                                    />
                                                </Fragment>
                                            ) : type === TransactionType.DerivativeTrade ? (
                                                <Fragment>
                                                    <div className="flex flex-grow w-full">
                                                        <NumberField
                                                            name="accruedInterest"
                                                            label="Accrued interest"
                                                            disabled={isSubmitting || isLocked || isDeleted}
                                                            changeTolerancePercent={values && values._id ? defaultFatFingerTolerance : null}
                                                            className=""
                                                        />
                                                        <NumberField
                                                            name="rounding"
                                                            label="Rounding"
                                                            className="ml-1"
                                                            disabled={isSubmitting || isLocked || isDeleted}
                                                        />
                                                    </div>
                                                    <SelectField
                                                        name="clearingHouseId"
                                                        label="Clearing house"
                                                        options={clearingHouses.map((d) => ({ key: d._id, value: d.name }))}
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        className=""
                                                    />
                                                    <SelectField
                                                        name="clearingBrokerId"
                                                        label="Clearing broker"
                                                        options={clearingBrokers.map((d) => ({ key: d._id, value: d.name }))}
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        className=""
                                                    />
                                                    <SelectField
                                                        name="agreementType"
                                                        label="Agreement type*"
                                                        options={agreementTypes}
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        className=""
                                                    />
                                                    <NullableDateField
                                                        name="carryOwnCostDate"
                                                        label="Carry own cost date"
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        className=""
                                                    />

                                                    {/* <SelectField name="clearingHouseId" className="col-12" /> */}
                                                </Fragment>
                                            ) : type === TransactionType.FundTrade ? (
                                                <Fragment>
                                                    <Fragment>
                                                        <SelectField
                                                            name="currency"
                                                            label="Currency"
                                                            options={currencies}
                                                            disabled={isSubmitting || isLocked || isDeleted}
                                                            className=""
                                                        />
                                                        <SelectField
                                                            key={`externalAccount-${clientId}`}
                                                            name="externalAccountId"
                                                            label={
                                                                values.clientId !== emptyObjectId &&
                                                                values.externalAccountId !== emptyObjectId ? (
                                                                    <Link
                                                                        to={
                                                                            "/parties/" +
                                                                            values.clientId +
                                                                            "/externalAccounts/" +
                                                                            values.externalAccountId
                                                                        }
                                                                    >
                                                                        {"External account"}
                                                                    </Link>
                                                                ) : (
                                                                    <div>{"External account"}</div>
                                                                )
                                                            }
                                                            options={externalAccounts.map((d) => ({ key: d._id, value: d.name }))}
                                                            disabled={
                                                                isSubmitting ||
                                                                isLocked ||
                                                                !values.clientId ||
                                                                values.clientId === emptyObjectId
                                                            }
                                                            className=""
                                                        />
                                                        <CheckBoxField
                                                            name="reinvestedDividend"
                                                            label="Reinvested dividend"
                                                            disabled={isSubmitting || isLocked || isDeleted}
                                                            className=""
                                                        />
                                                        <CheckBoxField
                                                            name="reinvestedRebate"
                                                            label="Reinvested rebate"
                                                            disabled={isSubmitting || isLocked || isDeleted}
                                                            className=""
                                                        />
                                                        <div className="flex flex-grow w-full">
                                                            <NumberField
                                                                name="fxRate"
                                                                label="Fx rate"
                                                                disabled={isSubmitting || isLocked || isDeleted}
                                                                changeTolerancePercent={
                                                                    values && values._id ? defaultFatFingerTolerance : null
                                                                }
                                                                className=""
                                                            />
                                                            <NumberField
                                                                name="quantity"
                                                                label="Quantity*"
                                                                className="ml-1"
                                                                disabled={isSubmitting || isLocked || isDeleted}
                                                                changeTolerancePercent={
                                                                    values && values._id ? defaultFatFingerTolerance : null
                                                                }
                                                            />
                                                            <NumberField
                                                                name="price"
                                                                label="Price"
                                                                className="ml-1"
                                                                disabled={isSubmitting || isLocked || isDeleted}
                                                                changeTolerancePercent={
                                                                    values && values._id ? defaultFatFingerTolerance : null
                                                                }
                                                            />
                                                        </div>
                                                    </Fragment>
                                                    <div className="flex flex-grow w-full">
                                                        <NumberField
                                                            name="commission"
                                                            label="Commission"
                                                            disabled={isSubmitting || isLocked || isDeleted}
                                                            changeTolerancePercent={values && values._id ? defaultFatFingerTolerance : null}
                                                            className=""
                                                        />
                                                        <NumberField
                                                            name="amount"
                                                            label="Amount"
                                                            className="ml-1"
                                                            disabled={isSubmitting || isLocked || isDeleted}
                                                            changeTolerancePercent={values && values._id ? defaultFatFingerTolerance : null}
                                                        />
                                                    </div>
                                                </Fragment>
                                            ) : type === TransactionType.Dividend ? (
                                                <Fragment>
                                                    <SelectField
                                                        key={`externalAccount-${clientId}`}
                                                        name="externalAccountId"
                                                        label={
                                                            values.clientId !== emptyObjectId &&
                                                            values.externalAccountId !== emptyObjectId ? (
                                                                <Link
                                                                    to={
                                                                        "/parties/" +
                                                                        values.clientId +
                                                                        "/externalAccounts/" +
                                                                        values.externalAccountId
                                                                    }
                                                                >
                                                                    {"External account"}
                                                                </Link>
                                                            ) : (
                                                                <div>{"External account"}</div>
                                                            )
                                                        }
                                                        options={externalAccounts.map((d) => ({ key: d._id, value: d.name }))}
                                                        disabled={true}
                                                        className=""
                                                    />
                                                    <div className="flex flex-wrap w-full my-2">
                                                        <CheckBoxField
                                                            name="dividendPaid"
                                                            label="Dividend paid"
                                                            className="w-auto sm:w-6/12"
                                                            disabled={isSubmitting || isLocked || isDeleted}
                                                        />
                                                        <CheckBoxField
                                                            name="taxRestitution"
                                                            label="Tax restitution"
                                                            className="w-auto sm:w-6/12 ml-4"
                                                            disabled={isSubmitting || isLocked || isDeleted}
                                                        />
                                                    </div>
                                                    <NumberField
                                                        name="amount"
                                                        label="Amount*"
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        changeTolerancePercent={values && values._id ? defaultFatFingerTolerance : null}
                                                        className=""
                                                    />
                                                    <NumberField
                                                        name="foreignTax"
                                                        label="Foreign tax"
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        changeTolerancePercent={values && values._id ? defaultFatFingerTolerance : null}
                                                        className=""
                                                    />
                                                    <NumberField
                                                        name="rounding"
                                                        label="Rounding"
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        className=""
                                                    />
                                                </Fragment>
                                            ) : type === TransactionType.Fee ? (
                                                <Fragment>
                                                    <NumberField
                                                        name="amount"
                                                        label="Amount*"
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        changeTolerancePercent={values && values._id ? defaultFatFingerTolerance : null}
                                                        className=""
                                                    />
                                                    <SelectField
                                                        name="itemType"
                                                        label="Fee type*"
                                                        options={feeItemTypes}
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        className=""
                                                    />
                                                    <SelectField
                                                        name="agreementType"
                                                        label="Agreement type"
                                                        options={agreementTypes}
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        className=""
                                                    />
                                                </Fragment>
                                            ) : type === TransactionType.Insert ? (
                                                <Fragment>
                                                    <CheckBoxField
                                                        name="insertSecurity"
                                                        className=""
                                                        label="Security (default cash)"
                                                        disabled={values._id ? true : isSubmitting || isLocked || isDeleted}
                                                    />
                                                    {values.insertSecurity ? (
                                                        <div className="flex flex-grow w-full">
                                                            <NumberField
                                                                name="quantity"
                                                                label="Quantity"
                                                                disabled={isSubmitting || isLocked || isDeleted}
                                                                changeTolerancePercent={
                                                                    values && values._id ? defaultFatFingerTolerance : null
                                                                }
                                                                className=""
                                                            />
                                                            <NumberField
                                                                name="price"
                                                                label="Price"
                                                                className="ml-1"
                                                                disabled={isSubmitting || isLocked || isDeleted}
                                                                changeTolerancePercent={
                                                                    values && values._id ? defaultFatFingerTolerance : null
                                                                }
                                                            />
                                                        </div>
                                                    ) : null}
                                                    <NumberField
                                                        name="amount"
                                                        label="Amount"
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        changeTolerancePercent={values && values._id ? defaultFatFingerTolerance : null}
                                                        className=""
                                                    />
                                                </Fragment>
                                            ) : type === TransactionType.Interest ? (
                                                <Fragment>
                                                    <SelectField
                                                        name="itemType"
                                                        label="Interest type*"
                                                        options={interestItemTypes}
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        className=""
                                                    />
                                                    <SelectField
                                                        name="agreementType"
                                                        label="Agreement type"
                                                        options={agreementTypes}
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        className=""
                                                    />
                                                    <div className="flex flex-grow w-full">
                                                        <NumberField
                                                            name="amount"
                                                            label="Amount*"
                                                            disabled={isSubmitting || isLocked || isDeleted}
                                                            changeTolerancePercent={values && values._id ? defaultFatFingerTolerance : null}
                                                            className=""
                                                        />
                                                        <NumberField
                                                            name="foreignTax"
                                                            label="Foreign tax"
                                                            className="ml-1"
                                                            disabled={isSubmitting || isLocked || isDeleted}
                                                            changeTolerancePercent={values && values._id ? defaultFatFingerTolerance : null}
                                                        />
                                                    </div>
                                                    <NumberField
                                                        name="rounding"
                                                        label="Rounding"
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        className=""
                                                    />
                                                </Fragment>
                                            ) : type === "CloseDerivativeTrade" ? (
                                                <Fragment>
                                                    <NumberField
                                                        name="amount"
                                                        label="Amount*"
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        changeTolerancePercent={values && values._id ? defaultFatFingerTolerance : null}
                                                        className=""
                                                    />
                                                </Fragment>
                                            ) : type === TransactionType.Collateral ? (
                                                <Fragment>
                                                    <div className="flex flex-grow w-full">
                                                        <NumberField
                                                            name="quantity"
                                                            label="Quantity*"
                                                            disabled={isSubmitting || isLocked || isDeleted}
                                                            changeTolerancePercent={values && values._id ? defaultFatFingerTolerance : null}
                                                            className=""
                                                        />
                                                        <SelectField
                                                            name="currency"
                                                            label="Currency*"
                                                            options={currencies}
                                                            className="ml-1"
                                                            disabled={isSubmitting || isLocked || isDeleted}
                                                        />
                                                    </div>
                                                    <SelectField
                                                        name="agreementType"
                                                        label="Agreement type*"
                                                        options={agreementTypes}
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        className=""
                                                    />
                                                    <div className="flex flex-grow w-full">
                                                        <CheckBoxField
                                                            name="initialMargin"
                                                            label="Initial margin"
                                                            disabled={isSubmitting || isLocked || isDeleted}
                                                            className=""
                                                        />
                                                        <CheckBoxField
                                                            name="loan"
                                                            label="Loan"
                                                            className="ml-1"
                                                            disabled={isSubmitting || isLocked || isDeleted}
                                                        />
                                                    </div>
                                                </Fragment>
                                            ) : null}

                                            {type === "CloseDerivativeTrade" || type === "ExpiryDerivativeTrade" ? (
                                                <Fragment>
                                                    <NullableDateField
                                                        name="carryOwnCostDate"
                                                        label="Carry own cost date"
                                                        disabled={isSubmitting || isDeleted}
                                                        className=""
                                                    />
                                                    <TransactionField
                                                        name="parentId"
                                                        label={
                                                            values.parentId && values.parentId !== emptyObjectId ? (
                                                                <Link to={"/transaction/" + values.parentId}>Parent transaction*</Link>
                                                            ) : (
                                                                "Parent transaction*"
                                                            )
                                                        }
                                                        options={
                                                            type === "ExpiryDerivativeTrade"
                                                                ? expiryDerivativeTransactions
                                                                : derivativeAndInsertDerivativeTransactions
                                                        }
                                                        disabled={values._id || isSubmitting || isLocked || isDeleted}
                                                        className=""
                                                    />
                                                </Fragment>
                                            ) : null}

                                            {(tradeType &&
                                                type !== "CloseDerivativeTrade" &&
                                                type !== "ExpiryDerivativeTrade" &&
                                                !values.insert) ||
                                            type === TransactionType.Interest ||
                                            type === TransactionType.Dividend ? (
                                                <NumberField
                                                    name="settlementAmount"
                                                    label="Settlement amount"
                                                    disabled={true}
                                                    className=""
                                                />
                                            ) : null}
                                            {values.payableReceivable ? (
                                                <NumberField
                                                    name="payableReceivable"
                                                    label="Payable receivable"
                                                    disabled={true}
                                                    className=""
                                                />
                                            ) : null}
                                        </div>
                                        <div className="flex flex-wrap -mx-2">
                                            {type === TransactionType.Transfer || type === "FxTransfer" ? (
                                                <Fragment>
                                                    <SearchListField
                                                        name="instrumentId"
                                                        label={
                                                            values.instrumentId in partyInstrumentsById ? (
                                                                <Link
                                                                    to={
                                                                        "/parties/" +
                                                                        values.clientId +
                                                                        "/instruments/" +
                                                                        values.instrumentId
                                                                    }
                                                                >
                                                                    Instrument*
                                                                </Link>
                                                            ) : (
                                                                "Instrument*"
                                                            )
                                                        }
                                                        options={
                                                            values.clientId && values.clientId !== emptyObjectId
                                                                ? instruments
                                                                : [{ _id: emptyObjectId, name: "Select instrument", description: "" }]
                                                        }
                                                        disabled={
                                                            isSubmitting ||
                                                            isLocked ||
                                                            !values.clientId ||
                                                            values.clientId === emptyObjectId
                                                        }
                                                        className=""
                                                    />
                                                    <div className="flex flex-grow w-full">
                                                        <NumberField
                                                            name="amount"
                                                            label="Amount*"
                                                            disabled={isSubmitting || isLocked || isDeleted}
                                                            changeTolerancePercent={values && values._id ? defaultFatFingerTolerance : null}
                                                            className=""
                                                        />
                                                        <SelectField
                                                            name="currency"
                                                            label="Currency*"
                                                            options={currencies}
                                                            className="ml-1"
                                                            disabled={isSubmitting || isLocked || isDeleted}
                                                        />
                                                    </div>
                                                </Fragment>
                                            ) : null}
                                            {type === TransactionType.Transfer || type === "FxTransfer" ? (
                                                <Fragment>
                                                    <SearchListField
                                                        name="secondInstrumentId"
                                                        label={
                                                            values.secondInstrumentId in partyInstrumentsById ? (
                                                                <Link
                                                                    to={
                                                                        "/parties/" +
                                                                        values.clientId +
                                                                        "/instruments/" +
                                                                        values.secondInstrumentId
                                                                    }
                                                                >
                                                                    Second instr.*
                                                                </Link>
                                                            ) : (
                                                                "Second instr.*"
                                                            )
                                                        }
                                                        options={
                                                            values.clientId && values.clientId !== emptyObjectId
                                                                ? instruments
                                                                : [
                                                                      {
                                                                          _id: emptyObjectId,
                                                                          name: "Select second instrument",
                                                                          description: ""
                                                                      }
                                                                  ]
                                                        }
                                                        disabled={
                                                            isSubmitting ||
                                                            isLocked ||
                                                            !values.clientId ||
                                                            values.clientId === emptyObjectId
                                                        }
                                                        className=""
                                                    />
                                                    <SelectField
                                                        name="secondAccountId"
                                                        label={
                                                            <Link
                                                                to={"/parties/" + values.clientId + "/accounts/" + values.secondAccountId}
                                                            >
                                                                {"Second account"}
                                                            </Link>
                                                        }
                                                        options={accounts.map((d) => ({
                                                            key: d._id,
                                                            value: `${d.name} ${d.description}`
                                                        }))}
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        className=""
                                                    />
                                                </Fragment>
                                            ) : null}
                                            {type === "FxTransfer" ? (
                                                <Fragment>
                                                    <div className="flex flex-grow w-full">
                                                        <NumberField
                                                            name="secondAmount"
                                                            label="Second amount*"
                                                            disabled={isSubmitting || isLocked || isDeleted}
                                                            changeTolerancePercent={values && values._id ? defaultFatFingerTolerance : null}
                                                            className=""
                                                        />
                                                        <SelectField
                                                            name="secondCurrency"
                                                            label="Second currency*"
                                                            className="ml-1"
                                                            options={currencies}
                                                            disabled={isSubmitting || isLocked || isDeleted}
                                                        />
                                                    </div>
                                                    <TransactionField
                                                        name="parentId"
                                                        label={
                                                            values.parentId && values.parentId !== emptyObjectId ? (
                                                                <Link to={"/transaction/" + values.parentId}>Parent transaction</Link>
                                                            ) : (
                                                                "Parent transaction"
                                                            )
                                                        }
                                                        options={derivativeAndInsertDerivativeTransactions}
                                                        disabled={isSubmitting || isLocked || isDeleted}
                                                        className=""
                                                    />
                                                </Fragment>
                                            ) : null}
                                        </div>
                                        <div className="flex flex-wrap -mx-2">
                                            <TextField
                                                name="description"
                                                label="Description"
                                                type="text"
                                                disabled={
                                                    isSubmitting ||
                                                    values.revertTransaction ||
                                                    values.type === TransactionType.CreateRedeem ||
                                                    isDeleted
                                                }
                                                className=""
                                            />
                                            {noExternalId || values.insert ? (
                                                <SelectField
                                                    name="status"
                                                    label="Status"
                                                    options={Object.keys(TransactionStatus)}
                                                    tooltips={TransactionStatusDescriptions}
                                                    disabled={isSubmitting || values.revertTransaction || isDeleted}
                                                    className=""
                                                />
                                            ) : null}
                                            {values._id && values.type !== TransactionType.CreateRedeem ? (
                                                <Fragment>
                                                    <TextField
                                                        name="error"
                                                        label="Error"
                                                        disabled={isSubmitting || values.revertTransaction || isDeleted}
                                                        className=""
                                                    />
                                                    <TextField
                                                        name="brokerTradeId"
                                                        label={
                                                            values.corporateAction && values.corporateAction._id ? (
                                                                <Link
                                                                    to={
                                                                        "/reconcile/corporateactions/corporateaction/" +
                                                                        values.corporateAction._id
                                                                    }
                                                                >
                                                                    Corporate action id
                                                                </Link>
                                                            ) : values.brokerTransaction && values.brokerTransaction._id ? (
                                                                <Link
                                                                    to={
                                                                        "/brokerconfirmations/brokertransaction/" +
                                                                        values.brokerTransaction._id
                                                                    }
                                                                >
                                                                    Broker transaction id
                                                                </Link>
                                                            ) : (
                                                                "Broker trade id"
                                                            )
                                                        }
                                                        disabled={isSubmitting || values.revertTransaction || isDeleted}
                                                        className=""
                                                    />
                                                    <TextField
                                                        name="uniqueTradeId"
                                                        label="UTI"
                                                        disabled={isSubmitting || values.revertTransaction || isDeleted}
                                                        className=""
                                                    />
                                                </Fragment>
                                            ) : null}
                                            {type === TransactionType.DerivativeTrade && !values._id ? (
                                                <Fragment>
                                                    <TextField
                                                        name="uniqueTradeId"
                                                        label="UTI*"
                                                        type="text"
                                                        disabled={isSubmitting || isDeleted}
                                                        className=""
                                                    />
                                                </Fragment>
                                            ) : null}
                                        </div>
                                        <div className="flex flex-wrap -mx-2">
                                            {!noExternalId && !values.insert ? (
                                                <Fragment>
                                                    <SelectField
                                                        name="status"
                                                        label="Status"
                                                        options={Object.keys(TransactionStatus)}
                                                        tooltips={TransactionStatusDescriptions}
                                                        disabled={isSubmitting || values.revertTransaction || isDeleted}
                                                        className=""
                                                    />
                                                    <SelectField
                                                        name="source"
                                                        label={"Source" + (noExternalId ? "" : "*")}
                                                        options={sources}
                                                        disabled={values._id || isDeleted || noExternalId ? true : false}
                                                        className=""
                                                    />
                                                    <TextField
                                                        name="externalId"
                                                        label={"External id" + (noExternalId ? "" : "*")}
                                                        type="text"
                                                        disabled={values._id || isDeleted || noExternalId ? true : false}
                                                        className=""
                                                    />
                                                </Fragment>
                                            ) : null}
                                            <TextField
                                                name="_id"
                                                label={
                                                    values._id ? (
                                                        <a href={"/transaction/" + values._id} target="_blank" rel="noopener noreferrer">
                                                            Id
                                                        </a>
                                                    ) : (
                                                        <div>Id</div>
                                                    )
                                                }
                                                type="text"
                                                disabled={true}
                                                className=""
                                            />
                                        </div>
                                        <div className="flex flex-wrap -mx-2">
                                            <div className="col">
                                                {values._id &&
                                                (values.type === TransactionType.Dividend || values.type === TransactionType.Interest) &&
                                                values.parentId &&
                                                values.parentId !== emptyObjectId ? (
                                                    <a href={"/transaction/" + values.parentId} target="_blank" rel="noopener noreferrer">
                                                        Parent transaction
                                                    </a>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap -mx-2">
                                            {(type === TransactionType.Dividend || type === TransactionType.Interest) && values._id ? (
                                                <CheckBoxField
                                                    name="revertTransaction"
                                                    label="Revert transaction and add new"
                                                    disabled={isSubmitting || isDeleted}
                                                    className=""
                                                />
                                            ) : null}
                                        </div>
                                        {values.revertTransaction &&
                                        (type === TransactionType.Dividend || type === TransactionType.Interest) ? (
                                            <>
                                                <div className="flex flex-wrap -mx-2">
                                                    <i style={{ color: "red" }}>{"NEW " + type.toUpperCase()}</i>
                                                </div>
                                                <div className="flex flex-wrap -mx-2">
                                                    <DateField
                                                        name="newValueDate"
                                                        label="Value date*"
                                                        disabled={isSubmitting || isDeleted}
                                                        className=""
                                                    />
                                                    <SelectField
                                                        name="newCurrency"
                                                        label="Currency*"
                                                        options={currencies}
                                                        disabled={isSubmitting || isDeleted}
                                                        className=""
                                                    />
                                                    <NumberField
                                                        name="newAmount"
                                                        label="Amount*"
                                                        className="w-full"
                                                        disabled={isSubmitting || isDeleted}
                                                        changeTolerancePercent={values && values._id ? defaultFatFingerTolerance : null}
                                                    />
                                                    <NumberField
                                                        name="newForeignTax"
                                                        label="Foreign tax"
                                                        disabled={isSubmitting || isDeleted}
                                                        changeTolerancePercent={values && values._id ? defaultFatFingerTolerance : null}
                                                        className=""
                                                    />
                                                    <NumberField
                                                        name="newRounding"
                                                        label="Rounding"
                                                        disabled={isSubmitting || isDeleted}
                                                        className=""
                                                    />
                                                    <SelectField
                                                        name="newStatus"
                                                        label="Status"
                                                        options={Object.keys(TransactionStatus)}
                                                        tooltips={TransactionStatusDescriptions}
                                                        disabled={isSubmitting || isDeleted}
                                                        className=""
                                                    />
                                                </div>
                                            </>
                                        ) : null}
                                        <section ref={alertRef} id="alertSection" tabIndex={-1} aria-live="polite">
                                            {alert.visible ? (
                                                <Alert variant={alert.color} onClose={onDismissAlert} dismissible>
                                                    {alert.message}
                                                </Alert>
                                            ) : null}
                                        </section>
                                        <SubmitButton
                                            disabled={isSubmitting || Object.keys(errors).length > 0 || isDeleted}
                                            label={values._id && !values.revertTransaction ? "Save" : "Create"}
                                            className="btn btn-primary"
                                        />
                                    </Form>
                                </div>

                                <div>
                                    {values._id && isValidMongoDBObjectID(values._id) && !values.revertTransaction ? (
                                        <div className="flex flex-wrap -mx-2 mt-3">
                                            <AttachmentTransactionForm transactionId={values._id} />
                                        </div>
                                    ) : null}
                                </div>
                            </Fragment>
                        );
                    }}
                </Formik>
            </div>
        </div>
    );
}

export function NewMiniTransactionButton({ page, queryArgMode }: { page: string; queryArgMode?: boolean }): React.ReactElement {
    const [, navigate] = useLocation();
    const [queryArgs, setQueryArgs] = useSearchParams();
    const createMiniTransactionTypes = cloneDeep(miniTransactionTypes);
    //CreateRedeem is only available for viewing + updating, not creating
    const index = createMiniTransactionTypes.indexOf("CreateRedeem", 0);
    createMiniTransactionTypes.splice(index, 1);

    if (queryArgMode) {
        return (
            <Dropdown align="end">
                <Dropdown.Toggle variant="primary" size="sm">
                    New
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {createMiniTransactionTypes.map((type, i) => (
                        <Dropdown.Item
                            key={i}
                            as="button"
                            onClick={() =>
                                setQueryArgs((prev) => {
                                    prev.set("id", "new" + type.toLowerCase());
                                    return prev;
                                })
                            }
                        >
                            {type}
                        </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
        );
    } else {
        return (
            <Dropdown align="end">
                <Dropdown.Toggle variant="primary" size="sm">
                    New
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {createMiniTransactionTypes.map((type, i) => (
                        <Dropdown.Item
                            key={i}
                            as="button"
                            onClick={() =>
                                navigate(`/${page}/new` + type.toLowerCase() + (queryArgs.toString() ? `?${queryArgs.toString()}` : ""))
                            }
                        >
                            {type}
                        </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
        );
    }
}

function CopyMiniTransactionButton({
    queryArgMode,
    type,
    values,
    copyValues
}: {
    queryArgMode?: boolean;
    type: TransactionType;
    values: MiniTransactionFormType;
    copyValues: Record<"current", MiniTransactionFormType>;
}): React.ReactElement {
    const [queryArgs, setQueryArgs] = useSearchParams();
    const [location, navigate] = useLocation();
    const pathname = location.split("?")[0].split("#")[0];
    const path = pathname.split("/");
    path.pop();

    path.push("new" + type.toLowerCase() + (queryArgs.toString() ? `?${queryArgs.toString()}` : ""));

    const newValues = cloneDeep(values);
    newValues._id = null;
    newValues.externalId = null;
    newValues.parentId = null;

    if (queryArgMode) {
        return (
            <Button
                className="mr-1 mb-2"
                variant="success"
                size="sm"
                onClick={() => {
                    copyValues.current = newValues;
                    setQueryArgs((prev) => {
                        prev.set("id", "new" + type.toLowerCase());
                        return prev;
                    });
                }}
            >
                Copy
            </Button>
        );
    } else {
        return (
            <Button
                className="mt-4 mr-1 mb-2"
                variant="success"
                size="sm"
                onClick={() => {
                    copyValues.current = newValues;
                    navigate(path.join("/"));
                }}
            >
                Copy
            </Button>
        );
    }
}
